import { z } from 'zod'
import { MoneySchema } from '@/schemas/shared/money'
import { OptionalTextSchema } from '@/schemas/shared/text'
import { CartSchema } from './cart'

/**
 * Schemas do cupom de desconto no carrinho. Contrato da API (Glofi.API/Controllers/DiscountCodeController.cs),
 * travado em tests/Glofi.API.Tests/Snapshots/Discount/*.shape.txt:
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `discountcode/apply-coupon` | POST | `{ coupon }` | `{ cart, discountCode }` |
 * | `discountcode/update-coupon` | PUT | `{ coupon }` | `{ cart, discountCode }` (troca o cupom aplicado) |
 * | `discountcode/remove-coupon` | POST | - | `{ cart, discountCode }` (o cupom que saiu) |
 * | `discountcode/list-suggestion-coupons` | GET | - | `{ suggestions }` |
 *
 * Erros: 409 `cartNotFound` (sem carrinho aberto); 400 com `message` ("Cupom não encontrado", "Erro ao aplicar o
 * cupom. {motivo da regra}", "Cupom inválido") e, no remove, 400 `couponError` quando não há cupom aplicado.
 * O cupom do checkout (`checkout/apply-coupon`, `checkout/remove-coupon`) é do domínio checkout.
 */

const text = OptionalTextSchema

/** Cupom como a API devolve (projeção do DiscountCodeDTO). Datas em ISO. */
export const CouponSchema = z.object({
    code: z.string(),
    description: text,
    /** Valor do desconto (em reais ou percentual, conforme o tipo de cálculo do cupom, que a API não manda). */
    value: MoneySchema,
    /** Teto do desconto em reais. */
    maxValue: MoneySchema,
    validateStart: z.string(),
    validateEnd: z.string(),
})

/** apply-coupon, update-coupon e remove-coupon. No remove, `discountCode` é o cupom removido (null se foi apagado). */
export const CouponResponseSchema = z.object({
    cart: CartSchema,
    discountCode: CouponSchema.nullish().transform((value) => value ?? null),
})

/** `GET discountcode/list-suggestion-coupons`: cupons marcados como sugestão no painel. */
export const CouponSuggestionsResponseSchema = z.object({
    suggestions: z
        .array(CouponSchema)
        .nullish()
        .transform((value) => value ?? []),
})

// ===== REQUISIÇÃO E FORMULÁRIO =====

/** `{ coupon }` do apply-coupon e do update-coupon. A API normaliza o código na busca (`codeNormalized`). */
export const ApplyCouponRequestSchema = z.object({
    coupon: z.string().trim().min(1),
})

/** Formulário do cupom (modal do carrinho). */
export const CouponFormSchema = z.object({
    coupon: z.string().trim().min(1, 'Informe o cupom'),
})

// ===== TIPOS =====

export type ICoupon = z.infer<typeof CouponSchema>
export type ICouponResponse = z.infer<typeof CouponResponseSchema>
export type ICouponSuggestionsResponse = z.infer<typeof CouponSuggestionsResponseSchema>
export type IApplyCouponRequest = z.infer<typeof ApplyCouponRequestSchema>
export type ICouponForm = z.infer<typeof CouponFormSchema>
